import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useSelector } from "react-redux";
import { getVesselSOGDraftData } from "../../../../services/vesselMachineryService";

const SOGDraftOverTime = ({ isLoading = false, voyageId, selectedEngine }) => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const vesselId = currentVessel?._id;
  const { selectedEngineId } = useSelector((state) => state.mainEngine);

  const stateConfig = {
    "At sea": { color: "#1a5276" },
    "At sea (drifting)": { color: "#d35400" },
    Maneuvering: { color: "#27ae60" },
    "Maneuvering (anchored)": { color: "#7d3c98" },
    "In Port": { color: "#117864" },
    "N/A": { color: "#cccccc" },
  };

  const getFirstState = (stateString) => {
    if (!stateString || stateString === "N/A" || stateString === "no-data")
      return "N/A";
    const states = stateString.split("|");
    return states[0]?.trim() || "N/A";
  };

  const fetchData = async (start, end) => {
    if (!vesselId || !start || !end) return;
    const engineId = (selectedEngine || selectedEngineId || "")
      .toString()
      .toUpperCase();

    setLoading(true);
    try {
      const data = await getVesselSOGDraftData(
        vesselId,
        engineId,
        voyageId ? null : start,
        voyageId ? null : end,
        voyageId || null
      );

      const rows = Array.isArray(data) ? data : data?.data || [];
      setChartData(rows);
    } catch (error) {
      console.error("Error fetching SOG/draft data:", error);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchData(startDate, endDate);
    }
  }, [
    vesselId,
    selectedEngine,
    startDate,
    endDate,
    voyageId,
    selectedEngineId,
  ]);

  // timeData normalized
  const timeData = Array.isArray(chartData) ? chartData : [];

  // Build SOG and Draft arrays (numbers or null)
  const sogPoints = timeData.map((item) => ({
    x: new Date(item[0]).getTime(),
    y: typeof item[1] === "number" ? item[1] : item[1] ? Number(item[1]) : null,
  }));
  const draftPoints = timeData.map((item) => ({
    x: new Date(item[0]).getTime(),
    y: typeof item[2] === "number" ? item[2] : item[2] ? Number(item[2]) : null,
  }));

  // Calculate Y-axis ranges
  const calculateYAxisRanges = () => {
    if (timeData.length === 0) {
      return {
        sog: { min: 0, max: 16, tickInterval: 4 },
        draft: { min: 0, max: 12, tickInterval: 1 },
      };
    }

    const sogValues = timeData
      .map((item) => item[1])
      .filter((val) => val != null && !isNaN(val));
    const draftValues = timeData
      .map((item) => item[2])
      .filter((val) => val != null && !isNaN(val));

    let sogMin = 0;
    let sogMax = sogValues.length > 0 ? Math.max(...sogValues) : 16;

    const sogPadding = sogMax * 0.1;
    sogMax = sogMax + sogPadding;

    const sogTickInterval = Math.ceil((sogMax - sogMin) / 4);

    let draftMin = 0;
    let draftMax = draftValues.length > 0 ? Math.max(...draftValues) : 12;

    const draftPadding = draftMax * 0.1; // 10% padding
    draftMax = draftMax + draftPadding;

    const draftTickInterval = (draftMax - draftMin) / 4;

    return {
      sog: {
        min: sogMin,
        max: Math.ceil(sogMax * 10) / 10,
        tickInterval: Math.max(0.5, Math.ceil(sogTickInterval * 2) / 2),
      },
      draft: {
        min: draftMin,
        max: Math.ceil(draftMax * 10) / 10,
        tickInterval: Math.max(0.1, Math.ceil(draftTickInterval * 10) / 10),
      },
    };
  };

  const yAxisRanges = calculateYAxisRanges();

  const vesselStateSeriesArray = useMemo(() => {
    const map = {};

    timeData.forEach((item) => {
      const ts = new Date(item[0]).getTime();

      const rawState = getFirstState(item[3]);
      const stateName = rawState ? `Vessel State: ${rawState}` : "N/A";

      const color = stateConfig[rawState]?.color || stateConfig["N/A"].color;

      if (!map[stateName]) {
        map[stateName] = {
          name: stateName,
          type: "line",
          data: [],
          dashStyle: "Dash",
          lineWidth: 3,
          color, // <-- correct color now
          marker: { enabled: true, radius: 4, symbol: "circle" },
          yAxis: 0,
          zIndex: 3,
        };
      }

      map[stateName].data.push({ x: ts, y: 0 });
    });

    return Object.values(map);
  }, [timeData]);

  // Build final series: vessel state series first (so they appear in legend), then SOG and Draft
  const allSeries = useMemo(() => {
    return [
      // vessel state series (one per state)
      ...vesselStateSeriesArray,

      // SOG
      {
        name: "SOG (kts)",
        data: sogPoints,
        yAxis: 0,
        type: "line",
        color: "#a93226",
        zIndex: 4,
        marker: { enabled: true, radius: 3 },
      },

      // Draft
      {
        name: "Mean Draft (m)",
        type: "spline",
        data: draftPoints,
        yAxis: 1,
        color: "#b7950b",
        dashStyle: "Solid",
        lineWidth: 2,
        zIndex: 5,
        marker: { enabled: true, radius: 3, symbol: "circle" },
      },
    ];
  }, [vesselStateSeriesArray, sogPoints, draftPoints]);

  const tooltipFormatter = function () {
    const date = new Date(this.x).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    let html = `<div style="font-weight:600;margin-bottom:5px">${date}</div>`;

    this.points.forEach((p) => {
      const isVesselState = p.series.options.dashStyle === "Dash";

      if (isVesselState) {
        html += `<div style="display:flex;justify-content:space-between;margin-bottom:3px">
        <span style="color:#666;display:flex;align-items:center">
          <span style="width:8px;height:8px;background:${p.color};margin-right:6px;border-radius:2px"></span>
          ${p.series.name}
        </span>
        <span style="font-weight:600"></span> <!-- No value shown -->
      </div>`;
      } else {
        html += `<div style="display:flex;justify-content:space-between;margin-bottom:3px">
        <span style="color:#666;display:flex;align-items:center">
          <span style="width:8px;height:8px;background:${
            p.color
          };margin-right:6px;border-radius:2px"></span>
          ${p.series.name}
        </span>
        <span style="font-weight:600">${p.y !== null ? p.y : "N/A"}</span>
      </div>`;
      }
    });

    return html;
  };

  // Build chartOptions (memoized)
  const chartOptions = useMemo(() => {
    return {
      chart: {
        type: "line",
        height: "500px",
        backgroundColor: "transparent",
        spacing: [20, 15, 15, 15],
      },
      title: { text: null },
        xAxis: {
            type: "datetime",
            dateTimeLabelFormats: {
                millisecond: "%b %e",
                second: "%b %e", 
                minute: "%b %e",
                hour: "%b %e",
                day: "%b %e",
                week: "%b %e",
                month: "%b %e",
                year: "%b %e"
            },
            title: { text: "Timeline", style: { color: "#666", fontSize: "12px" } },
            gridLineWidth: 1,
            gridLineColor: "#f0f0f0",
            lineColor: "#ccd6eb",
            lineWidth: 1,
            labels: {
                style: { color: "#666" },
                formatter: function() {
                    const date = new Date(this.value);
                    const formattedDate = Highcharts.dateFormat('%b %e', this.value);
                    
                    console.log('X-Axis Date:', {
                        timestamp: this.value,
                        formatted: date.toISOString(),
                        display: formattedDate,
                        axisPosition: this.pos
                    });
                    
                    return formattedDate;
                }
            },
            crosshair: { 
                width: 1, 
                color: "#e5e7eb", 
                dashStyle: "Solid" 
            },
        },
      yAxis: [
        {
          title: {
            text: "SOG (kts)",
            style: { color: "#666", fontSize: "12px" },
          },
          min: yAxisRanges.sog.min,
          max: yAxisRanges.sog.max,
          tickInterval: yAxisRanges.sog.tickInterval,
          gridLineWidth: 1,
          gridLineColor: "#f0f0f0",
          lineColor: "#ccd6eb",
          lineWidth: 1,
          labels: { style: { color: "#666" } },
          opposite: false,
        },
        {
          title: {
            text: "Mean Draft (m)",
            style: { color: "#666", fontSize: "12px" },
          },
          min: yAxisRanges.draft.min,
          max: yAxisRanges.draft.max,
          tickInterval: yAxisRanges.draft.tickInterval,
          opposite: true,
          gridLineWidth: 1,
          gridLineColor: "#f0f0f0",
        },
      ],
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: tooltipFormatter,
      },
      plotOptions: {
        series: {
          states: { hover: { halo: { size: 0 } } },
          dataGrouping: { enabled: true, groupPixelWidth: 10 },
        },
      },
      series: allSeries,
      legend: {
        enabled: true,
        labelFormatter: function () {
          return this.name;
        },
      },
      credits: { enabled: false },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSeries, yAxisRanges]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1>SOG & Mean Draft over Time</h1>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 relative">
        {(isLoading || loading) && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10 rounded-lg">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {timeData.length === 0 && !loading ? (
          <div>No data available</div>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        )}
      </div>
    </div>
  );
};

export default SOGDraftOverTime;
